import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Fab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { TopeDescuentoCliente, TopeDescuentoClienteRequest } from '../../types';
import { getEndOfDayISOString } from '../../utils/date';

// Función utilitaria local para mostrar la fecha en formato DD/MM/YYYY
function formatFechaLocal(fechaVigencia: string) {
  const date = new Date(fechaVigencia);
  return date.getDate().toString().padStart(2, '0') + '/' +
         (date.getMonth() + 1).toString().padStart(2, '0') + '/' +
         date.getFullYear();
}

// Función utilitaria para comparar solo la fecha (ignorando la hora y zona horaria)
function isVigente(fechaVigencia: string) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const vigencia = new Date(fechaVigencia);
  vigencia.setHours(0, 0, 0, 0);
  return vigencia >= hoy;
}

const TopesCliente: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTope, setEditingTope] = useState<TopeDescuentoCliente | null>(null);
  const [formData, setFormData] = useState<TopeDescuentoClienteRequest>({
    clienteId: '',
    topeDescuento: 0,
    fechaVigencia: new Date().toISOString().split('T')[0],
    activo: true,
  });

  // Agregar estado para el valor de texto del tope
  const [topeDescuentoText, setTopeDescuentoText] = useState('');
  const [filtro, setFiltro] = useState('');
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Consultar topes por cliente
  const { data: topes = [], isLoading, error } = useQuery({
    queryKey: ['topesCliente'],
    queryFn: () => apiService.obtenerTopesCliente(),
  });

  // Filtrado de topes según el texto ingresado
  const topesFiltrados = topes.filter((tope) => {
    const texto = filtro.toLowerCase();
    return (
      tope.clienteRazonSocial?.toLowerCase().includes(texto) ||
      tope.clienteId?.toLowerCase().includes(texto) ||
      String(tope.topeDescuento).toLowerCase().includes(texto) ||
      formatFechaLocal(tope.fechaVigencia).includes(texto) ||
      (tope.activo ? 'activo' : 'inactivo').includes(texto)
    );
  });

  // Mutaciones
  const createMutation = useMutation({
    mutationFn: (data: TopeDescuentoClienteRequest) => apiService.crearTopeCliente(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesCliente'] });
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.error('Error al crear tope:', error);
      alert(`Error al crear tope: ${error.response?.data?.message || error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TopeDescuentoClienteRequest }) =>
      apiService.actualizarTopeCliente(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['topesCliente'] });
      handleCloseDialog();
      console.log('Tope actualizado exitosamente:', data);
      // Opcional: mostrar mensaje de éxito
      // alert('Tope de descuento actualizado correctamente');
    },
    onError: (error: any) => {
      console.error('Error al actualizar tope:', error);
      // Solo mostrar error si no es un error de red o timeout
      if (error.code !== 'ERR_NETWORK' && !error.message.includes('timeout')) {
        alert(`Error al actualizar tope: ${error.response?.data?.message || error.message}`);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.eliminarTopeCliente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesCliente'] });
    },
  });

  const handleOpenDialog = (tope?: TopeDescuentoCliente) => {
    if (tope) {
      setEditingTope(tope);
      setFormData({
        clienteId: tope.clienteId,
        topeDescuento: tope.topeDescuento,
        fechaVigencia: tope.fechaVigencia.split('T')[0],
        activo: tope.activo,
      });
      setTopeDescuentoText(tope.topeDescuento.toString());
    } else {
      setEditingTope(null);
      setFormData({
        clienteId: '',
        topeDescuento: 0,
        fechaVigencia: new Date().toISOString().split('T')[0],
        activo: true,
      });
      setTopeDescuentoText('');
    }
    setErrorMensaje(null); // Limpiar mensaje de error al abrir
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTope(null);
    setFormData({
      clienteId: '',
      topeDescuento: 0,
      fechaVigencia: new Date().toISOString().split('T')[0],
      activo: true,
    });
    setTopeDescuentoText('');
    setErrorMensaje(null); // Limpiar mensaje de error al cerrar
  };

  const handleTopeDescuentoChange = (value: string) => {
    setTopeDescuentoText(value);
    
    // Validar que sea un número válido
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setFormData({ ...formData, topeDescuento: numValue });
    } else if (value === '' || value === '.') {
      // Permitir campo vacío o punto decimal
      setFormData({ ...formData, topeDescuento: 0 });
    }
  };

  const handleSubmit = () => {
    setErrorMensaje(null); // Limpiar mensaje de error previa
    // Validar existencia solo si es nuevo
    if (!editingTope) {
      const clienteIdForm = formData.clienteId.trim().toLowerCase();
      const topeExistente = topes.find(
        (tope: TopeDescuentoCliente) =>
          tope.clienteId.trim().toLowerCase() === clienteIdForm
      );
      if (topeExistente) {
        setErrorMensaje(
          `Ya existe un tope de descuento para el cliente con ID: ${topeExistente.clienteId}.`
        );
        return;
      }
    }
    const requestData = {
      ...formData,
      fechaVigencia: getEndOfDayISOString(formData.fechaVigencia),
      activo: formData.activo
    };

    if (editingTope) {
      updateMutation.mutate({ id: editingTope.id, data: requestData });
    } else {
      createMutation.mutate(requestData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tope de descuento?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error al cargar los topes de descuento por cliente
      </Alert>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Topes de Descuento por Cliente
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Tope
        </Button>
      </Box>
      {/* Filtro de búsqueda */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Filtrar"
          variant="outlined"
          size="small"
          fullWidth
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Buscar por cualquier campo..."
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>ID Cliente</TableCell>
              <TableCell align="right">Tope (%)</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topesFiltrados.map((tope) => (
              <TableRow key={tope.id}>
                <TableCell>{tope.clienteRazonSocial}</TableCell>
                <TableCell>{tope.clienteId}</TableCell>
                <TableCell align="right">
                  <Typography variant="h6" color="primary">
                    {tope.topeDescuento}%
                  </Typography>
                </TableCell>
                <TableCell>
                  {formatFechaLocal(tope.fechaVigencia)}
                </TableCell>
                <TableCell>
                  {isVigente(tope.fechaVigencia) ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Vigente"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon />}
                      label="Vencido"
                      color="warning"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {tope.activo ? (
                    <Chip label="Activo" color="success" size="small" />
                  ) : (
                    <Chip label="Inactivo" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(tope)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(tope.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTope ? 'Editar Tope de Descuento' : 'Nuevo Tope de Descuento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {errorMensaje && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {errorMensaje}
              </Alert>
            )}
            <TextField
              fullWidth
              label="ID del Cliente"
              value={formData.clienteId}
              onChange={(e) => setFormData({ ...formData, clienteId: e.target.value })}
              margin="normal"
              required
              disabled={!!editingTope}
            />
            <TextField
              fullWidth
              label="Tope de Descuento (%)"
              type="text"
              value={topeDescuentoText}
              onChange={(e) => handleTopeDescuentoChange(e.target.value)}
              margin="normal"
              required
              placeholder="0.00"
              helperText="Ingrese un valor entre 0 y 100"
              error={topeDescuentoText !== '' && (isNaN(parseFloat(topeDescuentoText)) || parseFloat(topeDescuentoText) < 0 || parseFloat(topeDescuentoText) > 100)}
            />
            <TextField
              fullWidth
              label="Fecha de Vigencia"
              type="date"
              value={formData.fechaVigencia}
              onChange={(e) => setFormData({ ...formData, fechaVigencia: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  color="primary"
                />
              }
              label="Tope Activo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.clienteId || createMutation.isPending || updateMutation.isPending || (topeDescuentoText !== '' && (isNaN(parseFloat(topeDescuentoText)) || parseFloat(topeDescuentoText) < 0 || parseFloat(topeDescuentoText) > 100))}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              editingTope ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB para agregar rápidamente */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog()}
      >
        <AddIcon />
      </Fab>
    </>
  );
};

export default TopesCliente;
